import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Globe, Users, Sprout, BookOpen, Stethoscope, HandHeart, 
  ArrowRight, Star, CheckCircle, ChevronLeft, ChevronRight, MapPin, 
  Handshake, Play, Shield, TrendingUp, Award, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

// Impact Statistics
const impactStats = [
  { value: "50+", label: "Countries Reached", icon: Globe },
  { value: "2M+", label: "Lives Impacted", icon: Users },
  { value: "500+", label: "Projects Completed", icon: CheckCircle },
  { value: "15+", label: "Years of Service", icon: Award }
];

// Core Programs
const programs = [
  { 
    icon: HandHeart, 
    title: 'Poverty Alleviation', 
    description: 'Economic empowerment through sustainable livelihoods and microfinance', 
    color: '#E08B7A',
    link: '/programs'
  },
  { 
    icon: BookOpen, 
    title: 'Education & Skills', 
    description: 'Quality education and vocational training for youth', 
    color: '#7BC4E2',
    link: '/programs/education'
  },
  { 
    icon: Sprout, 
    title: 'Climate Action', 
    description: 'Environmental sustainability and climate resilience', 
    color: '#4A7C59',
    link: '/programs/climate-action'
  },
  { 
    icon: Stethoscope, 
    title: 'Health & Well-being', 
    description: 'Community health programs and medical assistance', 
    color: '#E5B75F',
    link: '/programs/health'
  },
];

// Interactive donor impact programmes (slider data)
const impactProgrammes = [
  {
    id: 'food',
    label: 'Food Distribution',
    location: 'Botkyrka, Sweden',
    icon: HandHeart,
    color: '#E08B7A',
    unitCost: 4,
    unitLabel: 'meals',
    detail: 'delivered to families in Alby, Fittja & Hallunda-Norsborg'
  },
  {
    id: 'education',
    label: 'Education & Skills',
    location: 'East Africa',
    icon: BookOpen,
    color: '#7BC4E2',
    unitCost: 8,
    unitLabel: 'school days',
    detail: 'sponsored for children in rural learning hubs'
  },
  {
    id: 'youth',
    label: 'Youth Digital Bootcamp',
    location: 'Addis Ababa & Hawassa',
    icon: Users,
    color: '#E5B75F',
    unitCost: 35,
    unitLabel: 'training hours',
    detail: 'in digital & entrepreneurial skills for unemployed youth'
  },
  {
    id: 'climate',
    label: 'Climate Action',
    location: 'Reforestation Sites',
    icon: Sprout,
    color: '#4A7C59',
    unitCost: 2,
    unitLabel: 'trees planted',
    detail: 'restoring degraded land & sequestering carbon'
  },
  {
    id: 'health',
    label: 'Maternal & Child Health',
    location: 'Tigray & Afar',
    icon: Stethoscope,
    color: '#9DB9A3',
    unitCost: 15,
    unitLabel: 'people screened',
    detail: 'through community health awareness campaigns'
  }
];

// Trust indicators
const trustIndicators = [
  { title: "UN ECOSOC Status", description: "Consultative status with the United Nations" },
  { title: "Transparent Operations", description: "85% of donations go directly to programs" },
  { title: "Global Presence", description: "Operating in Africa, Europe, Americas & Asia" },
];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [donationAmount, setDonationAmount] = useState(50);
  const [activeProgrammeId, setActiveProgrammeId] = useState('food');

  const activeProgramme = impactProgrammes.find((p) => p.id === activeProgrammeId) || impactProgrammes[0];
  const impactCount = Math.max(1, Math.round(donationAmount / activeProgramme.unitCost));

  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?crop=entropy&cs=srgb&fm=jpg&q=85",
      title: "Every Child Deserves",
      highlight: "A Chance to Thrive",
      description: "Your donation provides education, nutrition, and hope to children in need."
    },
    {
      image: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?crop=entropy&cs=srgb&fm=jpg&q=85",
      title: "When Crisis Strikes",
      highlight: "We Respond",
      description: "Rapid humanitarian aid to communities affected by disasters and conflict."
    },
    {
      image: "https://images.unsplash.com/photo-1574943320219-553eb213f72d?crop=entropy&cs=srgb&fm=jpg&q=85",
      title: "Protecting Our Planet",
      highlight: "For Future Generations",
      description: "Climate action and sustainable development for vulnerable communities."
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  return (
    <div className="min-h-screen bg-white">
      
      {/* Hero Section - Emotional & Compelling */}
      <section className="relative h-[90vh] min-h-[600px] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="container-custom relative z-10 h-full flex items-center">
          <div className="max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-block bg-[#E08B7A] text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
                  Hope in Action. Together We Change Lives.
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Manrope'] leading-tight">
                  {heroSlides[currentSlide].title}
                  <span className="block text-[#E5B75F]">{heroSlides[currentSlide].highlight}</span>
                </h1>
                <p className="text-xl text-white/90 mb-8 max-w-lg">
                  {heroSlides[currentSlide].description}
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                      <Heart className="w-5 h-5 mr-2" />
                      Donate Now
                    </Button>
                  </a>
                  <Link to="/about">
                    <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-lg font-semibold">
                      Learn More
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-3 h-3 rounded-full transition-all ${
                i === currentSlide ? 'bg-[#E5B75F] w-8' : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Urgent Appeal Banner */}
      <section className="bg-gradient-to-r from-[#E08B7A] to-[#E5B75F] py-4">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-white animate-pulse" />
              <span className="text-white font-bold text-lg">Your Support Matters</span>
            </div>
            <span className="text-white/90">Join thousands of donors making a difference today</span>
            <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-[#E08B7A] hover:bg-white/90 rounded-full px-6 font-semibold">
                Give Today
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-16 bg-slate-900">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon className="w-10 h-10 text-[#E5B75F] mx-auto mb-3" />
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-slate-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {trustIndicators.map((item, index) => (
              <motion.div
                key={item.title}
                className="flex items-start gap-4"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 bg-[#4A7C59]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-[#4A7C59]" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-20 bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#E08B7A] font-semibold text-sm uppercase tracking-wider">Why Support OPAD</span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mt-4 mb-6">
                Your Donation Creates <span className="text-[#4A7C59]">Lasting Change</span>
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                For over 15 years, OPAD has been transforming lives across 50+ countries. 
                Every dollar you give goes directly to programs that empower communities, 
                educate children, and create sustainable futures.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "85% of donations go directly to programs",
                  "Transparent reporting and accountability",
                  "UN ECOSOC consultative status",
                  "Tax-deductible donations"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#4A7C59]" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Make a Donation
                </Button>
              </a>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              data-testid="impact-slider-card"
            >
              <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 border border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900">
                    See Your Impact
                  </h3>
                  <span className="bg-[#E5B75F]/15 text-[#A47A1F] px-3 py-1 rounded-full text-xs font-bold">LIVE</span>
                </div>
                <p className="text-sm text-slate-500 mb-5">Drag the slider — see what your gift unlocks.</p>

                {/* Programme tabs */}
                <div className="flex flex-wrap gap-2 mb-6" data-testid="impact-programme-tabs">
                  {impactProgrammes.map((p) => {
                    const isActive = p.id === activeProgrammeId;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setActiveProgrammeId(p.id)}
                        data-testid={`impact-programme-${p.id}`}
                        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition border ${
                          isActive ? 'text-white border-transparent' : 'text-slate-700 border-slate-200 hover:border-slate-300'
                        }`}
                        style={isActive ? { backgroundColor: p.color } : { backgroundColor: 'white' }}
                      >
                        <p.icon className="w-3.5 h-3.5" />
                        {p.label}
                      </button>
                    );
                  })}
                </div>

                {/* Slider */}
                <div className="mb-5">
                  <div className="flex items-end justify-between mb-2">
                    <span className="text-xs text-slate-500 font-medium">Your gift</span>
                    <div className="text-right">
                      <span className="text-4xl md:text-5xl font-extrabold tabular-nums" style={{ color: activeProgramme.color }} data-testid="impact-slider-amount">
                        ${donationAmount}
                      </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(Number(e.target.value))}
                    data-testid="impact-slider-input"
                    aria-label="Donation amount"
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, ${activeProgramme.color} 0%, ${activeProgramme.color} ${((donationAmount - 5) / 495) * 100}%, #e2e8f0 ${((donationAmount - 5) / 495) * 100}%, #e2e8f0 100%)`
                    }}
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                    <span>$5</span><span>$50</span><span>$100</span><span>$250</span><span>$500</span>
                  </div>
                </div>

                {/* Impact result */}
                <motion.div
                  key={`${activeProgrammeId}-${impactCount}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="rounded-2xl p-5 mb-5 border"
                  style={{ backgroundColor: `${activeProgramme.color}10`, borderColor: `${activeProgramme.color}40` }}
                  data-testid="impact-slider-result"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <activeProgramme.icon className="w-5 h-5" style={{ color: activeProgramme.color }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: activeProgramme.color }}>
                      {activeProgramme.location}
                    </span>
                  </div>
                  <p className="text-slate-900 leading-tight">
                    <span className="text-3xl md:text-4xl font-extrabold tabular-nums" style={{ color: activeProgramme.color }}>{impactCount.toLocaleString()}</span>
                    <span className="text-2xl md:text-3xl font-bold ml-2">{activeProgramme.unitLabel}</span>
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{activeProgramme.detail}</p>
                </motion.div>

                <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer" className="block">
                  <Button
                    className="w-full text-white py-6 text-lg font-semibold rounded-xl"
                    style={{ backgroundColor: activeProgramme.color }}
                    data-testid="impact-slider-donate-button"
                  >
                    Donate ${donationAmount} Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <p className="text-center text-slate-500 text-xs mt-3">
                  Secure payment via PayPal · 85% of every gift reaches programmes
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Programs */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-[#7BC4E2] font-semibold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mt-4 mb-4">
              Programs That <span className="text-[#E08B7A]">Transform Lives</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our comprehensive approach addresses the root causes of poverty through education, 
              healthcare, economic empowerment, and climate resilience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                className="group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={program.link}>
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all h-full">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${program.color}20` }}
                    >
                      <program.icon className="w-7 h-7" style={{ color: program.color }} />
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg mb-2">{program.title}</h3>
                    <p className="text-slate-600 text-sm">{program.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/programs">
              <Button variant="outline" className="border-2 border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white rounded-full px-8 py-6 text-lg font-semibold">
                View All Programs
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Story/Impact Section */}
      <section className="py-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#E5B75F] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#7BC4E2] rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#E5B75F] font-semibold text-sm uppercase tracking-wider">Real Stories</span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mt-4 mb-6">
                "OPAD gave my children a future I never thought possible."
              </h2>
              <p className="text-slate-300 text-lg mb-6">
                Maria, a single mother from rural Kenya, shares how OPAD's education program 
                transformed her family's life. Today, her two daughters are in school with 
                dreams of becoming doctors.
              </p>
              <p className="text-slate-400 mb-8">
                Stories like Maria's are possible because of donors like you. Every contribution, 
                no matter the size, creates ripples of positive change that last for generations.
              </p>
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#E5B75F] hover:bg-[#D4A64E] text-slate-900 rounded-full px-8 py-6 text-lg font-semibold">
                  Be Part of Her Story
                  <Heart className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1509099836639-18ba1795216d?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Children learning"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#4A7C59] rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">98%</div>
                    <div className="text-slate-500 text-sm">Program Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="py-20 bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="text-[#4A7C59] font-semibold text-sm uppercase tracking-wider">Join Us</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mt-4 mb-4">
              Ways to <span className="text-[#7BC4E2]">Get Involved</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-[#E08B7A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-[#E08B7A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Donate</h3>
              <p className="text-slate-600 mb-6">
                Your financial support directly funds programs that change lives.
              </p>
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-6">
                  Give Now
                </Button>
              </a>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 bg-[#7BC4E2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Volunteer</h3>
              <p className="text-slate-600 mb-6">
                Share your skills and time with communities that need you most.
              </p>
              <Link to="/get-involved">
                <Button className="bg-[#7BC4E2] hover:bg-[#6AB3D1] text-white rounded-full px-6">
                  Join Us
                </Button>
              </Link>
            </motion.div>

            <motion.div
              className="bg-white rounded-2xl p-8 shadow-lg text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-16 h-16 bg-[#4A7C59]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Handshake className="w-8 h-8 text-[#4A7C59]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Partner</h3>
              <p className="text-slate-600 mb-6">
                Organizations can amplify impact through strategic partnerships.
              </p>
              <Link to="/partnership">
                <Button className="bg-[#4A7C59] hover:bg-[#3A6B49] text-white rounded-full px-6">
                  Learn More
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#4A7C59] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#E5B75F]/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold font-['Manrope'] text-white mb-6">
              Together, We Can End Poverty
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
              Join OPAD in our mission to create a world where every person has the opportunity 
              to live with dignity, hope, and opportunity.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-[#4A7C59] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold shadow-lg">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#4A7C59] rounded-full px-10 py-6 text-lg font-semibold">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
